// services/AssetServices.ts
import { 
	PersonaAsset, 
	CreateAssetRequest, 
	UpdateAssetRequest, 
	GetAssetsRequest, 
	GetAssetsResponse,
	AssetType 
} from '../types';

class AssetServices {
	// Fetch assets for a persona
	async getAssets(request: GetAssetsRequest): Promise<GetAssetsResponse> {
		try {
			const url = new URL('/api/getAssets', window.location.origin);
			url.searchParams.append('persona_id', request.persona_id);
			if (request.type) {
				url.searchParams.append('type', request.type);
			}
			if (request.is_selected !== undefined) {
				url.searchParams.append('is_selected', request.is_selected.toString());
			}

			const response = await fetch(url.toString());
			if (!response.ok) {
				throw new Error(`Failed to fetch assets: ${response.statusText}`);
			}

			const data = await response.json();

			if (this.isGetAssetsResponse(data)) {
				return data;
			} else {
				console.error('Invalid data format received:', data);
				throw new Error('Invalid data format received from API');
			}
		} catch (error) {
			console.error('Error in getAssets:', error);
			throw error;
		}
	}

	// Create a new asset
	async createAsset(request: CreateAssetRequest & { persona_id: string }): Promise<PersonaAsset> {
		try {
			const response = await fetch('/api/createAsset', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json;charset=utf-8',
				},
				body: JSON.stringify(request),
			});

			if (!response.ok) {
				throw new Error(`Failed to create asset: ${response.statusText}`);
			}

			const data = await response.json();

			if (this.isPersonaAsset(data)) {
				return data;
			} else {
				console.error('Invalid data format received:', data);
				throw new Error('Invalid data format received from API after creating asset');
			}
		} catch (error) {
			console.error('Error in createAsset:', error);
			throw error;
		}
	}

	// Update an existing asset
	async updateAsset(request: UpdateAssetRequest): Promise<PersonaAsset> {
		try {
			const response = await fetch('/api/updateAsset', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json;charset=utf-8',
				},
				body: JSON.stringify(request),
			});

			if (!response.ok) {
				throw new Error(`Failed to update asset: ${response.statusText}`);
			}

			const data = await response.json();

			if (this.isPersonaAsset(data)) {
				return data;
			} else {
				console.error('Invalid data format received:', data);
				throw new Error('Invalid data format received from API after updating asset');
			}
		} catch (error) {
			console.error('Error in updateAsset:', error);
			throw error;
		}
	}

	// Delete an asset
	async deleteAsset(assetId: string): Promise<{ message: string; asset_id: string }> {
		try {
			const url = new URL('/api/deleteAsset', window.location.origin);
			url.searchParams.append('asset_id', assetId);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
			});

			if (!response.ok) {
				throw new Error(`Failed to delete asset: ${response.statusText}`);
			}

			const data = await response.json() as { message: string; asset_id: string };
			return data;
		} catch (error) {
			console.error('Error in deleteAsset:', error);
			throw error;
		}
	}

	// Get assets by type for a persona
	async getAssetsByType(personaId: string, type: AssetType): Promise<PersonaAsset[]> {
		const response = await this.getAssets({ persona_id: personaId, type });
		return response.assets;
	}

	// Get selected assets for a persona
	async getSelectedAssets(personaId: string): Promise<PersonaAsset[]> {
		const response = await this.getAssets({ persona_id: personaId, is_selected: true });
		return response.assets;
	}

	// Parse asset metadata from JSON string
	parseAssetMetadata(metadata: string): Record<string, any> {
		try {
			return JSON.parse(metadata || '{}');
		} catch (error) {
			console.warn('Failed to parse asset metadata:', error);
			return {};
		}
	}

	// Parse asset dependencies from JSON string
	parseAssetDependencies(dependencies: string): string[] {
		try {
			return JSON.parse(dependencies || '[]');
		} catch (error) {
			console.warn('Failed to parse asset dependencies:', error);
			return [];
		}
	}

	// Parse asset tags from JSON string
	parseAssetTags(tags: string): string[] {
		try {
			return JSON.parse(tags || '[]');
		} catch (error) {
			console.warn('Failed to parse asset tags:', error);
			return [];
		}
	}

	// Helper method to check if an object is a PersonaAsset
	private isPersonaAsset(data: any): data is PersonaAsset {
		return (
			typeof data === 'object' && data !== null &&
			'id' in data &&
			'type' in data &&
			'name' in data &&
			'content' in data &&
			'metadata' in data &&
			'dependencies' in data &&
			'tags' in data &&
			'version' in data &&
			'created_at' in data &&
			'updated_at' in data
		);
	}

	// Helper method to check if an object is a GetAssetsResponse
	private isGetAssetsResponse(data: any): data is GetAssetsResponse {
		return (
			typeof data === 'object' && data !== null &&
			'assets' in data &&
			'mappings' in data &&
			Array.isArray(data.assets) &&
			Array.isArray(data.mappings)
		);
	}
}

export default new AssetServices();